"use client";

import { useEffect, useRef } from "react";
import { trackException } from "@utils/exceptionTracker";
import { validatePrefetchImage } from "@utils/imageUtils";
import { toKebabCase } from "@utils/stringFormatters";


export default function ExceptionTracker({
  cityName,
  brandName,
  modelName,
  pageType,
  isMobile,
  dataChecks,
  carAvailability,
}: any) {
  const exceptionsTrackedRef = useRef<boolean>(false);

  useEffect(() => {
    if (exceptionsTrackedRef.current) {
      return;
    }
    exceptionsTrackedRef.current = true;
    const trackExceptions = async () => {
      const commonProps = {
        city_name: cityName,
        vehicle_make: brandName,
        vehicle_model: modelName,
        car_availability: carAvailability,
      };
      const trackingPromises = [];

      const checkAndRaiseException = (keyToCheck: string, key: string, condition: boolean, isSectionUnavailable: boolean) => {
        if (Object.keys(dataChecks).length === 0 || !Object.keys(dataChecks).includes(keyToCheck)) return;
        if (condition) {
          trackingPromises.push(trackException(isSectionUnavailable ? "section_unavailable" : "data_issue", key, pageType, commonProps));
        }
      };

      const prefetchFilePath = `${toKebabCase(brandName)}/${toKebabCase(
        modelName
      )}`;
      const baseUrl = `https://ackodrive-prod.ackoassets.com/image/${prefetchFilePath}/images_cdn`;
      
      // Use isMobile prop to check the correct image size (mobile = small, desktop = large)
      const sizeFolder = isMobile ? "small" : "large";
      
      const imageTag = "Hero-Transparent";
      const fullPrefetchUrl = `${baseUrl}/${sizeFolder}/${imageTag}.webp`;

      const isPrefetchImageValid = await validatePrefetchImage(fullPrefetchUrl);
      if (!isPrefetchImageValid) {
        trackingPromises.push(trackException(
          "data_issue",
          "image_unavailable",
          pageType,
          commonProps
        ));
      }

      const checks = [
        { key: "heroVideos", error: "expert_video_unavailable", path: "heroVideos.length" },
        { key: "expertRating", error: "expert_rating_unavailable", path: "expertRating" },
        { key: "standoutFeatures", error: "standout_unavailable", path: "standoutFeatures.features.length" },
        { key: "colorsData", error: "colours_unavailable", path: "colorsData.length" },
        { key: "modelTyresize", error: "variant_unavailable", path: "modelTyresize.tyreSizes.length" },
        { key: "compareCars", error: "car_unavailable", path: "compareCars.length" },
        { key: "similarCars", error: "similar_car_unavailable", path: "similarCars.length", isSection: true },
        { key: "sameBrandCars", error: "brand_car_unavailable", path: "sameBrandCars.length", isSection: true },
        { key: "faqData", error: "faq_unavailable", path: "faqData.results.length", isSection: true },
        { key: "variantSummary", error: "variant_summary_unavailable", path: "variantSummary", isSection: true },
        { key: "media", error: "expert_video_unavailable", path: "media.VARIANTS_AND_COLOURS.length", isSection: true }
      ];

      checks.forEach(({ key, error, path, isSection = false }) => {
        const value = path.split('.').reduce((obj, prop) => obj?.[prop], dataChecks);
        checkAndRaiseException(key, error, !value, isSection);
      });
      if (trackingPromises.length > 0) {
        return Promise.all(trackingPromises);
      }
      return Promise.resolve(null);
    };

    trackExceptions().catch(console.error);
  }, []);

  return null;
}
